import { getAllProducts } from 'bundles/enterprise-collections/components/getCollectionItemList';
import type { Product } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import { LEADERSHIP_RECS_COLLECTION_ID, PRODUCT_RECS_COLLECTION_ID } from 'bundles/program-personalized-tab/constants';
import type { EnterpriseCourseRecsForSkillIdQuery as EnterpriseCourseRecsForSkillIdQueryData } from 'bundles/program-personalized-tab/queries/__generated__/EnterpriseCourseRecsForSkillId';
import type { EnterpriseS12nRecsForSkillIdQuery as EnterpriseS12nRecsForSkillIdQueryData } from 'bundles/program-personalized-tab/queries/__generated__/EnterpriseS12nRecsForSkillId';
import type { CollectionTypeForRecsTab, ProductWithMetadata } from 'bundles/program-personalized-tab/types/sharedTypes';

const getCollectionId = (areLeadershipRecs?: boolean) =>
  areLeadershipRecs ? LEADERSHIP_RECS_COLLECTION_ID : PRODUCT_RECS_COLLECTION_ID;

export const courseRecsDataToCollectionType = (
  data?: EnterpriseCourseRecsForSkillIdQueryData,
  areLeadershipRecs?: boolean
): CollectionTypeForRecsTab => {
  const collectionResults = Object.values(data?.EntCollection || {}).filter(
    (value) => value !== 'EntCollectionQueries'
  );

  const allRecs = collectionResults.map((collection) => {
    if (collection.entities?.elements) {
      const coursesWithCollectionTrackingId: Array<Product | null> = collection.entities.elements.map((element) => {
        if (
          !element ||
          element.__typename !== 'ProductCard_ProductCard' ||
          element.productTypeAttributes.__typename !== 'ProductCard_Course'
        ) {
          return null;
        }

        const mappedCourse: ProductWithMetadata = {
          id: element.id,
          name: element.productTypeAttributes.canonical.name,
          slug: element.productTypeAttributes.canonical.slug,
          promoPhoto: element.productTypeAttributes.canonical.promoPhotoUrl,
          partners: { elements: element.productTypeAttributes.canonical.partners },
          courseTypeMetadata: {
            courseTypeMetadata: {
              __typename: element.productTypeAttributes.canonical.courseTypeMetadata.metadata.__typename,
            },
          },
          skills: element.productTypeAttributes.canonical.skillTags.map((skill) => skill.skillName),
          level: element.productTypeAttributes.canonical.difficultyLevel ?? undefined,
          recommender: { hash: collection.recommenderConfigurationHash ?? '', context: collection.context },
        };

        return {
          ...mappedCourse,
          collectionTrackingId: collection.enterpriseCollectionTrackingId,
        };
      });

      return getAllProducts({
        ...collection,
        title: collection.enterpriseCollectionTitle ?? '',
        description: { cml: { value: collection.description ?? '', dtdId: '' } },
        courses: { elements: coursesWithCollectionTrackingId },
        s12ns: null,
      });
    }

    return null;
  });

  const roundRobinRecs: ProductWithMetadata[] = [];
  const maxLength = Math.max(...allRecs.map((item) => item?.length || 0));
  const ids: string[] = [];

  for (let i = 0; i < maxLength; i += 1) {
    for (const collection of allRecs) {
      if (collection?.[i] && !ids.includes(collection[i].id) && collection[i].isCourse) {
        roundRobinRecs.push(collection[i].product);
        ids.push(collection[i].id);
      }
    }
  }

  const collection: CollectionTypeForRecsTab = {
    ...collectionResults[0],
    id: getCollectionId(areLeadershipRecs),
    title: '',
    trackId: getCollectionId(areLeadershipRecs),
    description: {},
    courses: { elements: roundRobinRecs },
    courseIds: ids,
    s12ns: null,
  };

  return collection;
};

// Keeping this function separate from the course one above, because while they are currently
// similar, we will change the source for s12n recs later.
export const s12nRecsDataToCollectionType = (
  data?: EnterpriseS12nRecsForSkillIdQueryData,
  areLeadershipRecs?: boolean
): CollectionTypeForRecsTab => {
  const collectionResults = Object.values(data?.EntCollection || {}).filter(
    (value) => value !== 'EntCollectionQueries'
  );

  const allRecs = collectionResults.map((collection) => {
    if (collection.entities?.elements) {
      const s12nsWithLogoAndCollectionTrackingId: Array<Product | null> = collection.entities.elements.map(
        (element) => {
          if (
            !element ||
            element.__typename !== 'ProductCard_ProductCard' ||
            element.productTypeAttributes.__typename !== 'ProductCard_Specialization'
          ) {
            return null;
          }

          const mappedS12n: ProductWithMetadata = {
            id: element.id,
            name: element.productTypeAttributes.canonical.name,
            slug: element.productTypeAttributes.canonical.slug,
            promoPhoto: element.productTypeAttributes.canonical.cardImageUrl,
            partners: { elements: element.productTypeAttributes.canonical.partners },
            skills: element.productTypeAttributes.canonical.skillTags.map((skill) => skill.skillName),
            level: element.productTypeAttributes.canonical.difficultyLevel,
            recommender: { hash: collection.recommenderConfigurationHash ?? '', context: collection.context },
          };

          return {
            ...mappedS12n,
            collectionTrackingId: collection.enterpriseCollectionTrackingId,
          };
        }
      );

      return getAllProducts({
        ...collection,
        title: collection.enterpriseCollectionTitle ?? '',
        description: { cml: { value: collection.description ?? '', dtdId: '' } },
        s12ns: { elements: s12nsWithLogoAndCollectionTrackingId },
        courses: null,
      });
    }

    return null;
  });

  const roundRobinRecs: Product[] = [];
  const maxLength = Math.max(...allRecs.map((item) => item?.length || 0));
  const ids: string[] = [];

  for (let i = 0; i < maxLength; i += 1) {
    for (const collection of allRecs) {
      if (collection?.[i] && !ids.includes(collection[i].id) && !collection[i].isCourse) {
        roundRobinRecs.push(collection[i].product);
        ids.push(collection[i].id);
      }
    }
  }

  const collection: CollectionTypeForRecsTab = {
    ...collectionResults[0],
    id: getCollectionId(areLeadershipRecs),
    title: '',
    trackId: getCollectionId(areLeadershipRecs),
    description: {},
    courses: null,
    s12ns: { elements: roundRobinRecs },
    s12nIds: ids,
    proCerts: { elements: [] },
  };

  return collection;
};
